// Google Sheets Job Data Fetcher
// This module fetches job listing data from a published Google Sheet CSV
// Sheet columns: Turkce is ilani adi, Ingilizce is ilani adi, Sirket,
//   Sehir (Turkce), Sehir (Ingilizce), Ulke (Turkce), Ulke (Ingilizce), link

// Published Google Sheet CSV URL (job listings tab)
const GOOGLE_SHEETS_JOBS_CSV_URL =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vSk-yQJ0ZkBJA3fzZgFLlza8iEUrRTprMjkvz5RZL9a2SVUu57jTVQBuzUFhRS0y0mNgYl851uNnLYH/pub?gid=1581505029&single=true&output=csv";

// Cache for storing fetched data
let cachedJobs = null;
let cacheTimestamp = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes cache

/**
 * Parse a single CSV line, handling quoted fields that may contain commas
 * @param {string} line - A single CSV line
 * @returns {string[]} Array of field values
 */
function parseCSVLine(line) {
  const fields = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        // Check for escaped quote ("")
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++; // skip next quote
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        fields.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
  }

  fields.push(current.trim());
  return fields;
}

/**
 * Fetch job listings from Google Sheets
 * @returns {Promise<Array>} Array of job objects
 */
export async function fetchJobData() {
  // Check cache first
  if (
    cachedJobs &&
    cacheTimestamp &&
    Date.now() - cacheTimestamp < CACHE_DURATION
  ) {
    return cachedJobs;
  }

  try {
    const response = await fetch(GOOGLE_SHEETS_JOBS_CSV_URL);
    if (!response.ok) {
      throw new Error("Failed to fetch job listings from Google Sheets");
    }

    const csvText = await response.text();

    // Split into lines and filter empty
    const lines = csvText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (lines.length < 2) {
      // Only header or empty
      return [];
    }

    // Parse header to get column indices
    const header = parseCSVLine(lines[0]);

    // Map spreadsheet column names to internal field names
    const headerMap = {
      "turkce is ilani adi": "titleTr",
      "ingilizce is ilani adi": "titleEn",
      "sirket": "company",
      "sehir (turkce)": "city",
      "sehir (ingilizce)": "cityEn",
      "ulke (turkce)": "country",
      "ulke (ingilizce)": "countryEn",
      "link": "link",
      "siteye ekle": "showOnSite",
    };

    // Build column index lookup: internal field name -> column index
    const colIndex = {};
    header.forEach((col, i) => {
      const normalized = col.toLowerCase().trim();
      const fieldName = headerMap[normalized];
      if (fieldName) {
        colIndex[fieldName] = i;
      }
    });

    // Expected internal fields
    const expectedFields = [
      "titleTr",
      "titleEn",
      "company",
      "city",
      "cityEn",
      "country",
      "countryEn",
      "link",
    ];

    // Validate that all expected fields were found
    const missingFields = expectedFields.filter(
      (field) => colIndex[field] === undefined
    );
    if (missingFields.length > 0) {
      console.warn(
        "Job sheet missing columns for fields:",
        missingFields,
        "- Found headers:",
        header
      );
    }

    // Parse data rows
    const jobs = [];
    for (let i = 1; i < lines.length; i++) {
      const fields = parseCSVLine(lines[i]);

      const getValue = (fieldName) => {
        const idx = colIndex[fieldName];
        return idx !== undefined && idx < fields.length ? fields[idx] : "";
      };

      const titleTr = getValue("titleTr");
      const company = getValue("company");

      // Skip rows with no title and no company (likely empty rows)
      if (!titleTr && !company) continue;

      // Only include rows where "Siteye Ekle" is ticked (TRUE)
      const showOnSite = getValue("showOnSite").toUpperCase();
      if (showOnSite !== "TRUE") continue;

      jobs.push({
        id: i,
        titleTr: titleTr,
        titleEn: getValue("titleEn"),
        company: company,
        city: getValue("city"),
        cityEn: getValue("cityEn"),
        country: getValue("country"),
        countryEn: getValue("countryEn"),
        link: getValue("link"),
      });
    }

    // Update cache
    cachedJobs = jobs;
    cacheTimestamp = Date.now();

    return jobs;
  } catch (error) {
    console.error("Error fetching job data from Google Sheets:", error);
    // Return cached data if available, even if expired
    if (cachedJobs) {
      return cachedJobs;
    }
    throw error;
  }
}

/**
 * Clear the job data cache to force fresh data fetch
 */
export function clearJobCache() {
  cachedJobs = null;
  cacheTimestamp = null;
}
